import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Clock, Users, ChefHat, Heart, Star } from 'lucide-react'
import { Card, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import useRecipeStore from '../../store/recipes'
import useAuthStore from '../../store/auth'
import { formatTime, formatDifficulty } from '../../lib/utils'
import { cn } from '../../lib/utils'

const RecipeCard = ({ recipe, className }) => {
  const { toggleFavorite, favorites } = useRecipeStore()
  const { isAuthenticated } = useAuthStore()
  const isFavorite = favorites.includes(recipe.id)

  const handleFavoriteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isAuthenticated) {
      toggleFavorite(recipe.id)
    }
  }

  const difficultyColors = {
    easy: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    hard: 'bg-red-100 text-red-800'
  }

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={cn("recipe-card", className)}
    >
      <Card className="overflow-hidden h-full">
        <Link to={`/recipes/${recipe.id}`} className="block">
          {/* Recipe Image */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={recipe.image || 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&auto=format'}
              alt={recipe.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />

            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Favorite Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm hover:bg-white/90"
              onClick={handleFavoriteClick}
            >
              <Heart
                className={cn(
                  "w-4 h-4 transition-colors",
                  isFavorite ? "fill-red-500 text-red-500" : "text-gray-600"
                )}
              />
            </Button>

            {/* Difficulty Badge */}
            <div className="absolute top-3 left-3">
              <span className={cn(
                "px-2 py-1 text-xs font-medium rounded-full",
                difficultyColors[recipe.difficulty] || difficultyColors.easy
              )}>
                {formatDifficulty(recipe.difficulty)}
              </span>
            </div>

            {/* Rating Badge */}
            {recipe.rating && (
              <div className="absolute bottom-3 left-3 flex items-center space-x-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs font-medium">{recipe.rating}</span>
              </div>
            )}
          </div>

          <CardContent className="p-4">
            {/* Recipe Title */}
            <h3 className="font-display font-semibold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {recipe.title}
            </h3>

            {/* Recipe Stats */}
            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{formatTime(recipe.cookTime || 30)}</span>
              </div>

              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{recipe.servings || 4} servings</span>
              </div>

              <div className="flex items-center space-x-1">
                <ChefHat className="w-4 h-4" />
                <span>{recipe.category || 'Main'}</span>
              </div>
            </div>

            {/* Recipe Description */}
            <p className="text-sm text-gray-600 line-clamp-2 mb-4">
              {recipe.description || 'A delicious recipe that will make your taste buds dance with joy.'}
            </p>

            {/* Chef Info */}
            {recipe.chef && (
              <div className="flex items-center space-x-2 pt-3 border-t border-gray-100">
                <img
                  src={recipe.chef.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(recipe.chef.name)}&background=f59e0b&color=fff`}
                  alt={recipe.chef.name}
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-xs text-gray-500 font-medium">
                  by {recipe.chef.name}
                </span>
              </div>
            )}
          </CardContent>
        </Link>
      </Card>
    </motion.div>
  )
}

export default RecipeCard